import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import WorkoutsPage from './pages/WorkoutsPage';
import NutritionPage from './pages/NutritionPage';
import HabitsPage from './pages/HabitsPage';
import MessagesPage from './pages/MessagesPage';
import CommunityPage from './pages/CommunityPage';
import SubscriptionPage from './pages/SubscriptionPage';
import SettingsPage from './pages/SettingsPage';
import MarketplacePage from './pages/MarketplacePage';
import TrainerDashboardPage from './pages/TrainerDashboardPage';
import MyTransformationsPage from './pages/MyTransformationsPage';
import WorkoutDetailPage from './pages/WorkoutDetailPage';
import MealPlanDetailPage from './pages/MealPlanDetailPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import FoodLoggingPage from './pages/FoodLoggingPage';
import FoodScannerPage from './pages/FoodScannerPage';
import LeaderboardPage from './pages/LeaderboardPage';
import CalendarPage from './pages/CalendarPage';
import CheckInsPage from './pages/CheckInsPage';
import ReferralsPage from './pages/ReferralsPage';
import GoogleFitPage from './pages/GoogleFitPage';
import BroadcastsPage from './pages/BroadcastsPage';
import TeamsPage from './pages/TeamsPage';
import WearablesPage from './pages/WearablesPage';
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage';
import BrandingPage from './pages/BrandingPage';
import CampaignsPage from './pages/CampaignsPage';
import ProgramsLibraryPage from './pages/ProgramsLibraryPage';
import ExerciseLibraryPage from './pages/ExerciseLibraryPage';
import FormsPage from './pages/FormsPage';
import ForumPage from './pages/ForumPage';
import FitnessToolsPage from './pages/FitnessToolsPage';
import WorkoutMusicPage from './pages/WorkoutMusicPage';
import ClientManagementPage from './pages/ClientManagementPage';
import ClientProfilePage from './pages/ClientProfilePage';
import ClientIntakeFormPage from './pages/ClientIntakeFormPage';

// Layout
import MainLayout from './components/layout/MainLayout';

// Theme Context
import { ThemeProvider } from './contexts/ThemeContext';

import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('bba_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error('Auth error:', error);
          localStorage.removeItem('bba_token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token: newToken, user: userData } = response.data;
    localStorage.setItem('bba_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { token: newToken, user: newUser } = response.data;
    localStorage.setItem('bba_token', newToken);
    setToken(newToken);
    setUser(newUser);
    return newUser;
  };

  const logout = () => {
    localStorage.removeItem('bba_token');
    setToken(null);
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Onboarding Check Route
const OnboardingCheckRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!user.onboarding_completed) {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="dark min-h-screen bg-background">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            
            {/* Onboarding */}
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            } />
            
            {/* Protected Routes with Layout */}
            <Route path="/dashboard" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <DashboardPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/workouts" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <WorkoutsPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/workouts/:workoutId" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <WorkoutDetailPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/nutrition" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <NutritionPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/nutrition/:planId" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <MealPlanDetailPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/habits" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <HabitsPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/my-progress" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <MyTransformationsPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/messages" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <MessagesPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/community" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <CommunityPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/subscription" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <SubscriptionPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/subscription/success" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <SubscriptionSuccessPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/marketplace" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <MarketplacePage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/settings" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <SettingsPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/trainer" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <TrainerDashboardPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/food-log" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <FoodLoggingPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/food-scanner" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <FoodScannerPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/leaderboard" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <LeaderboardPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/calendar" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <CalendarPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/checkins" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <CheckInsPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/referrals" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <ReferralsPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/google-fit" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <GoogleFitPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/announcements" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <BroadcastsPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/teams" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <TeamsPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/wearables" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <WearablesPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/analytics" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <AnalyticsDashboardPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/branding" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <BrandingPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/campaigns" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <CampaignsPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/programs" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <ProgramsLibraryPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/exercises" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <ExerciseLibraryPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/forms" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <FormsPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/forum" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <ForumPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/tools" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <FitnessToolsPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/workout-music" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <WorkoutMusicPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            {/* Client Management Routes */}
            <Route path="/clients" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <ClientManagementPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/clients/intake/new" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <ClientIntakeFormPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/clients/intake/:clientId" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <ClientIntakeFormPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/clients/:clientId" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <ClientProfilePage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            <Route path="/my-intake" element={
              <OnboardingCheckRoute>
                <MainLayout>
                  <ClientIntakeFormPage />
                </MainLayout>
              </OnboardingCheckRoute>
            } />
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: '#121212',
                border: '1px solid #27272A',
                color: '#fff'
              }
            }}
          />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
